<script lang="ts">
  import { authState, refreshCustomClaims } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { doc, getDoc, setDoc, updateDoc, collection, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import { type Business, type Warehouse, type UserConfig, type WarehouseFormData } from '../../lib/types';

  import BusinessList from './BusinessList.svelte';
  import BusinessDetail from './BusinessDetail.svelte';
  import WarehouseList from './WarehouseList.svelte';
  import LinkableWarehousesList from './LinkableWarehousesList.svelte';
  import OutsourcedWarehouses from './OutsourcedWarehouses.svelte';
  import ArchivedBusinesses from './ArchivedBusinesses.svelte';
  import BusinessFormModal from './BusinessFormModal.svelte';
  import WarehouseFormModal from './WarehouseFormModal.svelte';
  import WarehouseLinkModal from './WarehouseLinkModal.svelte';

  // Hardcoded outsourced warehouses that AccountAdmins can enable
  const OUTSOURCED_WAREHOUSES: Warehouse[] = [
    {
      id: 'MvGy16e7THOUudQCT3hD',
      name: 'MTM Logistics',
      address: 'Brisbane, QLD',
      isActive: true,
      allowedUserTypes: [],
      allowedUserIds: [],
      isOutsourced: true,
    },
    // Add more outsourced warehouses here as needed
  ];

  // State
  let businesses = $state<Business[]>([]);
  let archivedBusinesses = $state<Business[]>([]);
  let selectedBusiness = $state<Business | null>(null);
  let warehouses = $state<Warehouse[]>([]);
  let loading = $state(true);
  let saveError = $state('');

  // Default business for custom claims (used by Go backend)
  let defaultBusinessId = $state<string | null>(null);

  // UI state
  let showCreateBusinessModal = $state(false);
  let showEditBusinessModal = $state(false);
  let showAddWarehouseModal = $state(false);
  let showEditWarehouseModal = $state(false);
  let editingWarehouse = $state<Warehouse | null>(null);
  let showLinkWarehouseModal = $state(false);
  let linkingWarehouse = $state<Warehouse | null>(null);
  let linkingLinkedIds = $state<string[]>([]);
  let linkingPrimaryBusinessId = $state('');
  let linkingLoading = $state(false);

  // Track linked business counts per warehouse (for badge display)
  let warehouseLinkedCounts = $state<Record<string, number>>({});

  // Form state
  let formError = $state('');
  let formLoading = $state(false);

  // All warehouses keyed by business ID
  let allWarehouses = $state<Record<string, Warehouse[]>>({});

  // Track which outsourced warehouses are enabled for the current user/business
  let enabledOutsourcedIds = $state<Set<string>>(new Set());
  let outsourcedLoading = $state<Set<string>>(new Set()); // Track loading state per warehouse

  // Track linkable warehouses from other businesses
  let enabledLinkableIds = $state<Set<string>>(new Set());
  let linkableLoading = $state<Set<string>>(new Set());

  const industries = [
    'Manufacturing & Distribution',
    'Freight & Shipping',
    'Retail & E-commerce',
    'Food & Beverage',
    'Health & Beauty',
    'Technology',
    'Wholesale',
    'Other',
  ];

  // Get user document reference
  function getUserDocRef() {
    if (!authState.user) return null;
    return doc(db, 'users', authState.user.uid);
  }

  // Load user config from Firestore
  async function loadUserConfig() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as Partial<UserConfig>;

        // Load businesses
        if (data.businesses && Array.isArray(data.businesses)) {
          businesses = data.businesses.map(b => ({
            ...b,
            createdAt: b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt)
          }));
        }

        // Load archived businesses
        if (data.archivedBusinesses && Array.isArray(data.archivedBusinesses)) {
          archivedBusinesses = data.archivedBusinesses.map(b => ({
            ...b,
            createdAt: b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt)
          }));
        }

        // Load all warehouses
        if (data.warehouses) {
          allWarehouses = data.warehouses;
        }

        // Load default business ID (for custom claims)
        if (data.businessId) {
          defaultBusinessId = data.businessId;
        }

        // Set selected business
        if (data.selectedBusinessId && businesses.length > 0) {
          const found = businesses.find(b => b.id === data.selectedBusinessId);
          if (found) {
            selectedBusiness = found;
            warehouses = allWarehouses[found.id] || [];
          }
        } else if (businesses.length > 0) {
          selectedBusiness = businesses[0];
          warehouses = allWarehouses[businesses[0].id] || [];
        }
      }
    } catch (error: any) {
      console.error('Error loading user config:', error);
      saveError = 'Failed to load business data';
    } finally {
      loading = false;
    }
  }

  // Save user config to Firestore
  async function saveUserConfig() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      // Prepare businesses for Firestore (convert Date to ISO string)
      const businessesForSave = businesses.map(b => ({
        ...b,
        createdAt: b.createdAt instanceof Date ? b.createdAt.toISOString() : b.createdAt
      }));

      const archivedForSave = archivedBusinesses.map(b => ({
        ...b,
        createdAt: b.createdAt instanceof Date ? b.createdAt.toISOString() : b.createdAt
      }));

      await setDoc(userDocRef, {
        businesses: businessesForSave,
        archivedBusinesses: archivedForSave,
        selectedBusinessId: selectedBusiness?.id || null,
        businessId: defaultBusinessId || null, // Required for Go backend claims
        warehouses: allWarehouses,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      saveError = '';
    } catch (error: any) {
      console.error('Error saving user config:', error);
      saveError = 'Failed to save changes';
      throw error;
    }
  }

  // Load data on mount
  onMount(async () => {
    if (authState.user) {
      await loadUserConfig();
      // Load outsourced access after user config (needs selectedBusiness)
      await loadOutsourcedWarehouseAccess();
      // Load linkable warehouse access (warehouses from other businesses)
      await loadLinkableWarehouseAccess();
      // Load linked counts for warehouse badges
      loadWarehouseLinkedCounts();
    } else {
      loading = false;
    }
  });

  // Trigger the Go backend to refresh custom claims
  async function triggerClaimsRefresh() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      await updateDoc(userDocRef, {
        claimsSet: false, // This triggers the Go backend listener
        claimsRefreshRequestedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to trigger claims refresh:', error);
    }
  }

  // Sync accessSummary document (the single governing doc for Flutter security rules)
  async function syncAccessSummary() {
    if (!authState.user) return;

    const uid = authState.user.uid;
    const summaryRef = doc(db, 'users', uid, 'accessSummary', 'current');

    // Collect all active business IDs
    const activeBusinessIds = businesses.map(b => b.id);

    // Collect all warehouse IDs across all businesses
    const allWarehouseIds = new Set<string>();
    for (const bizWarehouses of Object.values(allWarehouses)) {
      for (const w of bizWarehouses) {
        if (w.id) allWarehouseIds.add(w.id);
      }
    }

    // Add enabled outsourced warehouse IDs
    for (const id of enabledOutsourcedIds) {
      allWarehouseIds.add(id);
    }

    const warehouseIds = [...allWarehouseIds];

    try {
      await setDoc(summaryRef, {
        readBusinessIds: activeBusinessIds,
        writeBusinessIds: activeBusinessIds,
        readWarehouseIds: warehouseIds,
        writeWarehouseIds: warehouseIds,
        isAccountAdmin: true,
        isSuperAdmin: false,
        isClientUser: false,
        defaultBusinessId: defaultBusinessId || activeBusinessIds[0] || '',
        defaultWarehouseId: warehouseIds[0] || '',
        modules: [],
        computedAt: new Date(),
        version: 1,
      });
    } catch (error) {
      console.error('Failed to sync access summary:', error);
    }
  }

  // Sync accessibleBusinessIds to userDefaults (read by Flutter app's business selector)
  async function syncUserDefaults() {
    if (!authState.user) return;
    const userDefaultsRef = doc(db, 'users', authState.user.uid, 'settings', 'userDefaults');

    const activeBusinessIds = businesses.map(b => b.id);
    const defaultBiz = businesses.find(b => b.id === defaultBusinessId);

    await setDoc(userDefaultsRef, {
      accessibleBusinessIds: activeBusinessIds,
      defaultBusinessId: defaultBusinessId || activeBusinessIds[0] || null,
      defaultBusinessName: defaultBiz?.name || null,
      updatedAt: new Date().toISOString(),
      updatedBy: authState.user.uid,
    }, { merge: true });
  }

  // Check if a business is the default
  function isDefaultBusiness(businessId: string): boolean {
    return defaultBusinessId === businessId;
  }

  // ===========================================================================
  // Business CRUD
  // ===========================================================================

  async function selectBusiness(business: Business) {
    selectedBusiness = business;
    warehouses = allWarehouses[business.id] || [];

    // Reload outsourced warehouse access for the new business
    await loadOutsourcedWarehouseAccess();
    // Reload linkable warehouse access for the new business
    await loadLinkableWarehouseAccess();
    // Reload linked counts for warehouses in this business
    loadWarehouseLinkedCounts();

    // Save selected business preference
    try {
      await saveUserConfig();
    } catch (error) {
      console.error('Failed to save business selection:', error);
    }
  }

  // Set a business as the default for custom claims
  async function setAsDefaultBusiness() {
    if (!selectedBusiness || defaultBusinessId === selectedBusiness.id) return;

    defaultBusinessId = selectedBusiness.id;

    try {
      await saveUserConfig();
      // Trigger claims refresh so Go backend picks up the new businessId
      await triggerClaimsRefresh();
    } catch (error) {
      console.error('Failed to set default business:', error);
      saveError = 'Failed to set default business';
    }
  }

  async function handleCreateBusiness(data: { name: string; industry: string }) {
    if (!data.name.trim()) {
      formError = 'Business name is required';
      return;
    }

    if (!authState.user?.uid) {
      formError = 'You must be logged in to create a business';
      return;
    }

    formLoading = true;
    formError = '';

    try {
      // Refresh claims to ensure we have the latest permissions
      await refreshCustomClaims();

      const newBusiness: Business = {
        id: crypto.randomUUID(),
        name: data.name.trim(),
        industry: data.industry || null,
        createdAt: new Date(),
      };

      // Write to businessdata collection (for Flutter app)
      const businessDocRef = doc(db, 'businessdata', newBusiness.id);
      await setDoc(businessDocRef, {
        name: newBusiness.name,
        business_name: newBusiness.name, // Alternative field name
        industry: newBusiness.industry || null,
        ownerId: authState.user.uid,
        createdAt: new Date().toISOString(),
        createdBy: authState.user.uid,
        isActive: true,
      });

      businesses = [...businesses, newBusiness];
      selectedBusiness = newBusiness;
      warehouses = [];
      allWarehouses[newBusiness.id] = [];

      // Auto-set as default if this is the first business or no default is set
      if (!defaultBusinessId || businesses.length === 1) {
        defaultBusinessId = newBusiness.id;
        // Trigger claims refresh
        await triggerClaimsRefresh();
      }

      await saveUserConfig();
      await syncUserDefaults();
      await syncAccessSummary();
      showCreateBusinessModal = false;
    } catch (error: any) {
      console.error('Error creating business:', error);
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        formError = 'Permission denied. Please try logging out and back in, then try again.';
      } else {
        formError = 'Failed to create business. Please try again.';
      }
    } finally {
      formLoading = false;
    }
  }

  async function handleEditBusiness(data: { name: string; industry: string }) {
    if (!selectedBusiness) return;

    if (!data.name.trim()) {
      formError = 'Business name is required';
      return;
    }

    formLoading = true;
    formError = '';

    try {
      const updated: Business = {
        ...selectedBusiness,
        name: data.name.trim(),
        industry: data.industry || null,
      };

      // Update in businessdata collection
      const businessDocRef = doc(db, 'businessdata', selectedBusiness.id);
      await updateDoc(businessDocRef, {
        name: updated.name,
        business_name: updated.name,
        industry: updated.industry || null,
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      businesses = businesses.map(b => b.id === updated.id ? updated : b);
      selectedBusiness = updated;

      await saveUserConfig();
      showEditBusinessModal = false;
    } catch (error: any) {
      console.error('Error updating business:', error);
      formError = 'Failed to update business. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  // Toggle business active status
  async function toggleBusinessActive() {
    if (!selectedBusiness) return;
    const business = selectedBusiness;
    const newIsActive = !(business.isActive !== false); // Default to true if undefined

    try {
      // Update in businessdata collection
      const businessDocRef = doc(db, 'businessdata', business.id);
      await updateDoc(businessDocRef, {
        isActive: newIsActive,
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      // Update local state
      const updated = { ...business, isActive: newIsActive };
      businesses = businesses.map(b => b.id === business.id ? updated : b);

      if (selectedBusiness?.id === business.id) {
        selectedBusiness = updated;
      }

      await saveUserConfig();
    } catch (error) {
      console.error('Failed to toggle business status:', error);
      saveError = 'Failed to update business status';
    }
  }

  // Archive a business (soft delete)
  async function handleArchiveBusiness() {
    if (!selectedBusiness) return;
    const business = selectedBusiness;
    if (!confirm(`Archive "${business.name}"? It will be hidden from the app but can be restored later.`)) return;

    try {
      // Update in businessdata collection
      const businessDocRef = doc(db, 'businessdata', business.id);
      await updateDoc(businessDocRef, {
        isArchived: true,
        isActive: false,
        updatedAt: new Date().toISOString(),
        archivedAt: new Date().toISOString(),
        archivedBy: authState.user?.uid,
      });

      // Move to archived list
      const archivedBusiness = { ...business, isArchived: true, isActive: false };
      archivedBusinesses = [...archivedBusinesses, archivedBusiness];

      // Remove from active businesses list
      businesses = businesses.filter(b => b.id !== business.id);

      // If the archived business was selected, clear selection or select another
      if (selectedBusiness?.id === business.id) {
        if (businesses.length > 0) {
          selectedBusiness = businesses[0];
          warehouses = allWarehouses[businesses[0].id] || [];
        } else {
          selectedBusiness = null;
          warehouses = [];
        }
      }

      await saveUserConfig();
      await syncUserDefaults();
      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to archive business:', error);
      saveError = 'Failed to archive business';
    }
  }

  // Unarchive a business (restore from archive)
  async function handleUnarchiveBusiness(business: Business) {
    try {
      // Update in businessdata collection
      const businessDocRef = doc(db, 'businessdata', business.id);
      await updateDoc(businessDocRef, {
        isArchived: false,
        isActive: true,
        updatedAt: new Date().toISOString(),
        restoredAt: new Date().toISOString(),
        restoredBy: authState.user?.uid,
      });

      // Move back to active list
      const restoredBusiness = { ...business, isArchived: false, isActive: true };
      businesses = [...businesses, restoredBusiness];

      // Remove from archived list
      archivedBusinesses = archivedBusinesses.filter(b => b.id !== business.id);

      // Auto-select the restored business
      selectedBusiness = restoredBusiness;
      warehouses = allWarehouses[restoredBusiness.id] || [];

      await saveUserConfig();
      await syncUserDefaults();
      await loadOutsourcedWarehouseAccess();
      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to unarchive business:', error);
      saveError = 'Failed to restore business';
    }
  }

  // ===========================================================================
  // Warehouse CRUD
  // ===========================================================================

  async function handleAddWarehouse(data: WarehouseFormData) {
    if (!data.name.trim()) {
      formError = 'Warehouse name is required';
      return;
    }

    if (!selectedBusiness) {
      formError = 'No business selected';
      return;
    }

    formLoading = true;
    formError = '';

    try {
      const warehouseId = crypto.randomUUID();
      const newWarehouse: Warehouse = {
        id: warehouseId,
        name: data.name.trim(),
        contactPerson: data.contactPerson.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        mobile: data.mobile.trim(),
        addressLine1: data.addressLine1.trim(),
        addressLine2: data.addressLine2.trim(),
        suburb: data.suburb.trim(),
        postcode: data.postcode.trim(),
        state: data.state.trim(),
        country: data.country.trim(),
        address: data.address.trim(),
        isActive: true,
        ownerId: authState.user?.uid,
        allowedUserTypes: [],
        allowedUserIds: [],
      };

      // Write to warehousedata collection (matches WarehouseEntity model)
      const warehouseDocRef = doc(db, 'warehousedata', warehouseId);
      await setDoc(warehouseDocRef, {
        warehouseName: newWarehouse.name,
        address: newWarehouse.address,
        isActive: newWarehouse.isActive,
        isArchived: false,
        isDisabled: false,
        isOutsourced: false,
        ownerId: authState.user?.uid,
        businessId: selectedBusiness.id,
        contactId: warehouseId,
        enabledBusinessIds: [selectedBusiness.id],
        allowedUserTypes: [],
        allowedUserIds: [authState.user?.uid],
        createdAt: new Date().toISOString(),
        createdBy: authState.user?.uid,
      });

      // Create matching location contact with same document ID (matches ContactModel)
      const contactDocRef = doc(db, 'businessdata', selectedBusiness.id, 'contacts', warehouseId);
      await setDoc(contactDocRef, {
        businessId: selectedBusiness.id,
        businessName: newWarehouse.name,
        contactName: newWarehouse.contactPerson ?? '',
        emailAddress: newWarehouse.email ?? '',
        phoneNumber: newWarehouse.phone ?? '',
        mobile: newWarehouse.mobile ?? '',
        fax: '',
        website: '',
        addressLine1: newWarehouse.addressLine1 ?? '',
        addressLine2: newWarehouse.addressLine2 ?? '',
        suburb: newWarehouse.suburb ?? '',
        state: newWarehouse.state ?? '',
        postcode: newWarehouse.postcode ?? '',
        country: newWarehouse.country ?? 'Australia',
        contactType: 'location',
        status: 'active',
        isArchived: false,
        priority: 'medium',
        notes: '',
        abn: '',
        acn: '',
        isGstRegistered: false,
        createdAt: new Date().toISOString(),
        createdBy: authState.user?.uid,
        updatedBy: '',
        tags: [],
        category: '',
      });

      warehouses = [...warehouses, newWarehouse];
      allWarehouses[selectedBusiness.id] = warehouses;

      // Track the initial linked count (1 = owning business)
      warehouseLinkedCounts = { ...warehouseLinkedCounts, [warehouseId]: 1 };

      await saveUserConfig();

      // Set locationId on user doc so claims include the default warehouse
      const userDocRef = getUserDocRef();
      if (userDocRef) {
        await updateDoc(userDocRef, {
          locationId: warehouseId,
          locationName: newWarehouse.name,
        });
        await triggerClaimsRefresh();
      }

      // Write defaultWarehouseId to userDefaults (read by Flutter app)
      if (authState.user) {
        const userDefaultsRef = doc(db, 'users', authState.user.uid, 'settings', 'userDefaults');
        await setDoc(userDefaultsRef, {
          defaultWarehouseId: warehouseId,
          defaultWarehouseName: newWarehouse.name,
          updatedAt: new Date().toISOString(),
          updatedBy: authState.user.uid,
        }, { merge: true });
      }

      await syncAccessSummary();
      showAddWarehouseModal = false;
    } catch (error: any) {
      console.error('Error adding warehouse:', error);
      formError = 'Failed to add warehouse. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  async function handleEditWarehouse(data: WarehouseFormData) {
    if (!editingWarehouse || !selectedBusiness) return;

    if (!data.name.trim()) {
      formError = 'Warehouse name is required';
      return;
    }

    formLoading = true;
    formError = '';

    try {
      // Update in warehousedata collection (matches WarehouseEntity model)
      const warehouseDocRef = doc(db, 'warehousedata', editingWarehouse.id);
      await updateDoc(warehouseDocRef, {
        warehouseName: data.name.trim(),
        address: data.address.trim(),
        contactId: editingWarehouse.id,
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      // Update matching location contact (matches ContactModel)
      const contactDocRef = doc(db, 'businessdata', selectedBusiness.id, 'contacts', editingWarehouse.id);
      await updateDoc(contactDocRef, {
        businessName: data.name.trim(),
        contactName: data.contactPerson.trim(),
        emailAddress: data.email.trim(),
        phoneNumber: data.phone.trim(),
        mobile: data.mobile.trim(),
        addressLine1: data.addressLine1.trim(),
        addressLine2: data.addressLine2.trim(),
        suburb: data.suburb.trim(),
        state: data.state.trim(),
        postcode: data.postcode.trim(),
        country: data.country.trim(),
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      warehouses = warehouses.map(w =>
        w.id === editingWarehouse!.id
          ? {
              ...w,
              name: data.name.trim(),
              contactPerson: data.contactPerson.trim(),
              email: data.email.trim(),
              phone: data.phone.trim(),
              mobile: data.mobile.trim(),
              addressLine1: data.addressLine1.trim(),
              addressLine2: data.addressLine2.trim(),
              suburb: data.suburb.trim(),
              postcode: data.postcode.trim(),
              state: data.state.trim(),
              country: data.country.trim(),
              address: data.address.trim(),
            }
          : w
      );
      allWarehouses[selectedBusiness.id] = warehouses;

      await saveUserConfig();
      showEditWarehouseModal = false;
      editingWarehouse = null;
    } catch (error: any) {
      console.error('Error updating warehouse:', error);
      formError = 'Failed to update warehouse. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  async function toggleWarehouseActive(warehouse: Warehouse) {
    if (!selectedBusiness) return;

    const newIsActive = !warehouse.isActive;
    const newStatus = newIsActive ? 'active' : 'inactive';

    try {
      // Update in warehousedata collection
      const warehouseDocRef = doc(db, 'warehousedata', warehouse.id);
      await updateDoc(warehouseDocRef, {
        isActive: newIsActive,
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      // Update matching location contact status
      const contactDocRef = doc(db, 'businessdata', selectedBusiness.id, 'contacts', warehouse.id);
      await updateDoc(contactDocRef, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.uid,
      });

      warehouses = warehouses.map(w =>
        w.id === warehouse.id ? { ...w, isActive: newIsActive } : w
      );
      allWarehouses[selectedBusiness.id] = warehouses;

      await saveUserConfig();
    } catch (error) {
      console.error('Failed to toggle warehouse status:', error);
      // Revert on error
      warehouses = warehouses.map(w =>
        w.id === warehouse.id ? { ...w, isActive: !newIsActive } : w
      );
    }
  }

  async function handleArchiveWarehouse(warehouse: Warehouse) {
    if (!confirm(`Archive warehouse "${warehouse.name}"? It will be hidden from the app but data will be preserved.`)) return;
    if (!selectedBusiness) return;

    const previousWarehouses = [...warehouses];

    try {
      const warehouseDocRef = doc(db, 'warehousedata', warehouse.id);
      await updateDoc(warehouseDocRef, {
        isArchived: true,
        isActive: false,
        updatedAt: new Date().toISOString(),
        archivedAt: new Date().toISOString(),
        archivedBy: authState.user?.uid,
      });

      warehouses = warehouses.filter(w => w.id !== warehouse.id);
      allWarehouses[selectedBusiness.id] = warehouses;

      await saveUserConfig();
      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to archive warehouse:', error);
      warehouses = previousWarehouses;
    }
  }

  // ===========================================================================
  // Outsourced Warehouse Functions
  // ===========================================================================

  async function loadOutsourcedWarehouseAccess() {
    if (!authState.user || !selectedBusiness) {
      enabledOutsourcedIds = new Set();
      return;
    }

    const claims = authState.customClaims;
    if (!claims?.businessId) {
      enabledOutsourcedIds = new Set();
      return;
    }

    const businessId = selectedBusiness.id;
    const enabled = new Set<string>();

    for (const warehouse of OUTSOURCED_WAREHOUSES) {
      try {
        const warehouseDocRef = doc(db, 'warehousedata', warehouse.id);
        const docSnap = await getDoc(warehouseDocRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const enabledBusinessIds: string[] = data.enabledBusinessIds || [];
          if (enabledBusinessIds.includes(businessId)) {
            enabled.add(warehouse.id);
          }
        }
      } catch (error) {
        console.debug(`Could not check access for warehouse ${warehouse.id}:`, error);
      }
    }

    enabledOutsourcedIds = enabled;
  }

  async function enableOutsourcedWarehouse(warehouseId: string) {
    if (!authState.user || !selectedBusiness) return;

    const businessId = selectedBusiness.id;
    outsourcedLoading = new Set([...outsourcedLoading, warehouseId]);

    try {
      const warehouseDocRef = doc(db, 'warehousedata', warehouseId);
      const docSnap = await getDoc(warehouseDocRef);

      if (docSnap.exists()) {
        await updateDoc(warehouseDocRef, {
          enabledBusinessIds: arrayUnion(businessId),
          isOutsourced: true,
          isActive: true,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const outsourcedWarehouse = OUTSOURCED_WAREHOUSES.find(w => w.id === warehouseId);
        if (outsourcedWarehouse) {
          await setDoc(warehouseDocRef, {
            warehouseName: outsourcedWarehouse.name,
            name: outsourcedWarehouse.name,
            address: outsourcedWarehouse.address,
            isActive: outsourcedWarehouse.isActive,
            ownerId: null,
            allowedUserTypes: [],
            allowedUserIds: [],
            isOutsourced: true,
            enabledBusinessIds: [businessId],
            createdAt: new Date().toISOString(),
          });
        }
      }

      enabledOutsourcedIds = new Set([...enabledOutsourcedIds, warehouseId]);
      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to enable outsourced warehouse:', error);
      saveError = 'Failed to enable warehouse. Please try again.';
    } finally {
      outsourcedLoading = new Set([...outsourcedLoading].filter(id => id !== warehouseId));
    }
  }

  async function disableOutsourcedWarehouse(warehouseId: string) {
    if (!authState.user || !selectedBusiness) return;

    const businessId = selectedBusiness.id;
    outsourcedLoading = new Set([...outsourcedLoading, warehouseId]);

    try {
      const warehouseDocRef = doc(db, 'warehousedata', warehouseId);
      await updateDoc(warehouseDocRef, {
        enabledBusinessIds: arrayRemove(businessId),
        updatedAt: new Date().toISOString(),
      });

      enabledOutsourcedIds = new Set([...enabledOutsourcedIds].filter(id => id !== warehouseId));
      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to disable outsourced warehouse:', error);
      saveError = 'Failed to disable warehouse. Please try again.';
    } finally {
      outsourcedLoading = new Set([...outsourcedLoading].filter(id => id !== warehouseId));
    }
  }

  async function toggleOutsourcedWarehouse(warehouseId: string) {
    if (enabledOutsourcedIds.has(warehouseId)) {
      await disableOutsourcedWarehouse(warehouseId);
    } else {
      await enableOutsourcedWarehouse(warehouseId);
    }
  }

  // ===========================================================================
  // Linkable Warehouses (from other businesses)
  // ===========================================================================

  interface LinkableWarehouse {
    id: string;
    name: string;
    address?: string;
    ownerBusinessId: string;
    ownerBusinessName: string;
  }

  // Compute warehouses from other businesses that can be linked to the selected one
  let linkableWarehouses = $derived.by(() => {
    if (!selectedBusiness) return [];

    const result: LinkableWarehouse[] = [];
    for (const [bizId, bizWarehouses] of Object.entries(allWarehouses)) {
      if (bizId === selectedBusiness.id) continue;
      const ownerBusiness = businesses.find(b => b.id === bizId);
      if (!ownerBusiness) continue;

      for (const w of bizWarehouses) {
        result.push({
          id: w.id,
          name: w.name,
          address: w.address,
          ownerBusinessId: bizId,
          ownerBusinessName: ownerBusiness.name,
        });
      }
    }
    return result;
  });

  async function loadLinkableWarehouseAccess() {
    if (!selectedBusiness) {
      enabledLinkableIds = new Set();
      return;
    }

    const businessId = selectedBusiness.id;
    const enabled = new Set<string>();

    for (const warehouse of linkableWarehouses) {
      try {
        const warehouseDocRef = doc(db, 'warehousedata', warehouse.id);
        const snap = await getDoc(warehouseDocRef);
        if (snap.exists()) {
          const enabledBusinessIds: string[] = snap.data()?.enabledBusinessIds || [];
          if (enabledBusinessIds.includes(businessId)) {
            enabled.add(warehouse.id);
          }
        }
      } catch (error) {
        console.debug(`Could not check linkable access for warehouse ${warehouse.id}:`, error);
      }
    }

    enabledLinkableIds = enabled;
  }

  async function toggleLinkableWarehouse(warehouseId: string) {
    if (!selectedBusiness) return;

    const businessId = selectedBusiness.id;
    linkableLoading = new Set([...linkableLoading, warehouseId]);

    try {
      const warehouseDocRef = doc(db, 'warehousedata', warehouseId);

      if (enabledLinkableIds.has(warehouseId)) {
        // Unlink
        await updateDoc(warehouseDocRef, {
          enabledBusinessIds: arrayRemove(businessId),
          updatedAt: new Date().toISOString(),
        });
        enabledLinkableIds = new Set([...enabledLinkableIds].filter(id => id !== warehouseId));
      } else {
        // Link
        await updateDoc(warehouseDocRef, {
          enabledBusinessIds: arrayUnion(businessId),
          updatedAt: new Date().toISOString(),
        });
        enabledLinkableIds = new Set([...enabledLinkableIds, warehouseId]);
      }

      await syncAccessSummary();
    } catch (error) {
      console.error('Failed to toggle linkable warehouse:', error);
      saveError = 'Failed to update warehouse link. Please try again.';
    } finally {
      linkableLoading = new Set([...linkableLoading].filter(id => id !== warehouseId));
    }
  }

  // ===========================================================================
  // Warehouse Linking (multi-business)
  // ===========================================================================

  async function loadWarehouseLinks(warehouseId: string): Promise<string[]> {
    try {
      const warehouseDocRef = doc(db, 'warehousedata', warehouseId);
      const snap = await getDoc(warehouseDocRef);
      if (snap.exists()) {
        return snap.data()?.enabledBusinessIds || [];
      }
    } catch (error) {
      console.error('Failed to load warehouse links:', error);
    }
    return [];
  }

  async function openLinkWarehouseModal(warehouse: Warehouse) {
    if (!selectedBusiness) return;

    linkingWarehouse = warehouse;
    linkingPrimaryBusinessId = selectedBusiness.id;
    linkingLoading = true;
    showLinkWarehouseModal = true;

    const ids = await loadWarehouseLinks(warehouse.id);
    linkingLinkedIds = ids;
    linkingLoading = false;
  }

  async function handleLinkWarehouse(businessId: string) {
    if (!linkingWarehouse) return;

    const previousIds = [...linkingLinkedIds];
    linkingLinkedIds = [...linkingLinkedIds, businessId];

    try {
      const warehouseDocRef = doc(db, 'warehousedata', linkingWarehouse.id);
      await updateDoc(warehouseDocRef, {
        enabledBusinessIds: arrayUnion(businessId),
        updatedAt: new Date().toISOString(),
      });

      // Update the count in local state
      warehouseLinkedCounts = {
        ...warehouseLinkedCounts,
        [linkingWarehouse.id]: linkingLinkedIds.length,
      };
    } catch (error) {
      console.error('Failed to link warehouse:', error);
      linkingLinkedIds = previousIds;
      saveError = 'Failed to link warehouse. Please try again.';
    }
  }

  async function handleUnlinkWarehouse(businessId: string) {
    if (!linkingWarehouse) return;
    if (businessId === linkingPrimaryBusinessId) return;

    const previousIds = [...linkingLinkedIds];
    linkingLinkedIds = linkingLinkedIds.filter(id => id !== businessId);

    try {
      const warehouseDocRef = doc(db, 'warehousedata', linkingWarehouse.id);
      await updateDoc(warehouseDocRef, {
        enabledBusinessIds: arrayRemove(businessId),
        updatedAt: new Date().toISOString(),
      });

      // Update the count in local state
      warehouseLinkedCounts = {
        ...warehouseLinkedCounts,
        [linkingWarehouse.id]: linkingLinkedIds.length,
      };
    } catch (error) {
      console.error('Failed to unlink warehouse:', error);
      linkingLinkedIds = previousIds;
      saveError = 'Failed to unlink warehouse. Please try again.';
    }
  }

  function closeLinkModal() {
    showLinkWarehouseModal = false;
    linkingWarehouse = null;
    linkingLinkedIds = [];
  }

  // Load linked counts for all warehouses of the selected business
  async function loadWarehouseLinkedCounts() {
    if (!selectedBusiness) return;

    const currentWarehouses = allWarehouses[selectedBusiness.id] || [];
    const counts: Record<string, number> = {};

    for (const warehouse of currentWarehouses) {
      try {
        const ids = await loadWarehouseLinks(warehouse.id);
        counts[warehouse.id] = ids.length;
      } catch {
        counts[warehouse.id] = 0;
      }
    }

    warehouseLinkedCounts = counts;
  }

  // ===========================================================================
  // Modal handlers
  // ===========================================================================

  function openEditWarehouseModal(warehouse: Warehouse) {
    editingWarehouse = warehouse;
    formError = '';
    showEditWarehouseModal = true;
  }

  function closeBusinessModal() {
    showCreateBusinessModal = false;
    showEditBusinessModal = false;
    formError = '';
  }

  function closeWarehouseModal() {
    showAddWarehouseModal = false;
    showEditWarehouseModal = false;
    editingWarehouse = null;
    formError = '';
  }

  function handleBusinessFormSubmit(data: { name: string; industry: string }) {
    if (showCreateBusinessModal) {
      handleCreateBusiness(data);
    } else {
      handleEditBusiness(data);
    }
  }

  function handleWarehouseFormSubmit(data: WarehouseFormData) {
    if (showAddWarehouseModal) {
      handleAddWarehouse(data);
    } else {
      handleEditWarehouse(data);
    }
  }
</script>

<section class="business-section">
  {#if saveError}
    <div class="error-banner">{saveError}</div>
  {/if}

  {#if !loading && businesses.length > 0 && !defaultBusinessId}
    <div class="warning-banner default-business-warning">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <div class="warning-content">
        <strong>No default business set</strong>
        <p>Select a business and click "Set Default" to enable access in the EQUOS app.</p>
      </div>
    </div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading business data...</p>
    </div>
  {:else}
    <!-- Two-panel layout -->
    <div class="two-panel-layout">
      <!-- Left Panel: Business List -->
      <BusinessList
        {businesses}
        selectedBusinessId={selectedBusiness?.id ?? null}
        {defaultBusinessId}
        onselect={selectBusiness}
        oncreatenew={() => { formError = ''; showCreateBusinessModal = true; }}
      />

      <!-- Right Panel: Business Details & Warehouses -->
      <main class="business-detail-panel">
        {#if !selectedBusiness}
          <div class="no-selection-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3>Select a Business</h3>
            <p>Choose a business from the list to view and manage its warehouses.</p>
          </div>
        {:else}
          <BusinessDetail
            business={selectedBusiness}
            isDefault={isDefaultBusiness(selectedBusiness.id)}
            ownerEmail={authState.user?.email ?? ''}
            onedit={() => { formError = ''; showEditBusinessModal = true; }}
            onsetdefault={setAsDefaultBusiness}
            ontoggleactive={toggleBusinessActive}
            onarchive={handleArchiveBusiness}
          />

          <!-- Warehouses Section -->
          <WarehouseList
            {warehouses}
            onadd={() => { formError = ''; showAddWarehouseModal = true; }}
            onedit={openEditWarehouseModal}
            ontoggleactive={toggleWarehouseActive}
            onarchive={handleArchiveWarehouse}
            onlink={openLinkWarehouseModal}
            linkedCounts={warehouseLinkedCounts}
          />

          <!-- Linkable Warehouses from Other Businesses -->
          <LinkableWarehousesList
            warehouses={linkableWarehouses}
            selectedBusinessName={selectedBusiness.name}
            enabledIds={enabledLinkableIds}
            loadingIds={linkableLoading}
            onToggle={toggleLinkableWarehouse}
          />

          <!-- Outsourced Warehouses Section -->
          <OutsourcedWarehouses
            outsourcedWarehouses={OUTSOURCED_WAREHOUSES}
            enabledIds={enabledOutsourcedIds}
            loadingIds={outsourcedLoading}
            ontoggle={toggleOutsourcedWarehouse}
          />
        {/if}

        <!-- Archived Businesses Section -->
        <ArchivedBusinesses
          {archivedBusinesses}
          onrestore={handleUnarchiveBusiness}
        />
      </main>
    </div>
  {/if}
</section>

<!-- Business Create/Edit Modal -->
<BusinessFormModal
  isOpen={showCreateBusinessModal || showEditBusinessModal}
  mode={showCreateBusinessModal ? 'create' : 'edit'}
  business={selectedBusiness}
  isLoading={formLoading}
  error={formError}
  {industries}
  onsubmit={handleBusinessFormSubmit}
  onclose={closeBusinessModal}
/>

<!-- Warehouse Add/Edit Modal -->
<WarehouseFormModal
  isOpen={showAddWarehouseModal || showEditWarehouseModal}
  mode={showAddWarehouseModal ? 'add' : 'edit'}
  warehouse={editingWarehouse}
  isLoading={formLoading}
  error={formError}
  onsubmit={handleWarehouseFormSubmit}
  onclose={closeWarehouseModal}
/>

<!-- Warehouse Link Modal -->
<WarehouseLinkModal
  isOpen={showLinkWarehouseModal}
  warehouse={linkingWarehouse}
  {businesses}
  linkedBusinessIds={linkingLinkedIds}
  primaryBusinessId={linkingPrimaryBusinessId}
  isLoading={linkingLoading}
  onlink={handleLinkWarehouse}
  onunlink={handleUnlinkWarehouse}
  onclose={closeLinkModal}
/>

<style>
  .business-section {
    background: var(--color-white);
    border-radius: var(--radius-md);
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
  }

  .warning-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .warning-banner svg {
    width: 24px;
    height: 24px;
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .warning-content strong {
    display: block;
    color: #92400e;
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .warning-content p {
    margin: 0;
    color: #a16207;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .two-panel-layout {
    display: flex;
    gap: 1.5rem;
    min-height: 500px;
  }

  .business-detail-panel {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .no-selection-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
    border: 2px dashed #e5e7eb;
  }

  .no-selection-state .empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 1.5rem;
    color: #ccc;
    background: none;
  }

  .no-selection-state .empty-icon svg {
    width: 100%;
    height: 100%;
    color: #ccc;
  }

  .no-selection-state h3 {
    margin: 0 0 0.5rem;
    color: #333;
    font-size: 1.1rem;
  }

  .no-selection-state p {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
    max-width: 300px;
  }

  @media (max-width: 768px) {
    .two-panel-layout {
      flex-direction: column;
      gap: 1rem;
    }

    .no-selection-state {
      min-height: 200px;
    }
  }
</style>
